import { AvatarIdT, SidekickNameT } from "types/avatar"
import { MSKType } from "types/sponsor"
import { TConversation, TMessage } from "types/transcript"

type UserColorsT = {
  background: string
  stroke: string
}

// Static role chat colors - these should be moved to the theme system for full light/dark support
const YouColors: UserColorsT = {
  background: "#303030",
  stroke: "#191015",
}

export const MskColors: UserColorsT = {
  background: "#B8E3FF",
  stroke: "#A8D3EF",
}

const UnknownUserColors: UserColorsT = {
  background: "#EEEECC",
  stroke: "#DDDDBB",
}

// Dynamicly assigned colors
export const CHAT_BUBBLE_COLORS: UserColorsT[] = [
  {
    background: "#C0E8D8",
    stroke: "#BDDDD4",
  },
  {
    background: "#DAD3E9",
    stroke: "#D1CADE",
  },
  {
    background: "#dbebf6",
    stroke: "#C8D6E0",
  },
  {
    background: "#FFE5CC",
    stroke: "#FFD6B8",
  },
  {
    background: "#FFCCDD",
    stroke: "#EEBBCC",
  },
] as const

export interface ChatU {
  name: SidekickNameT
  colors: UserColorsT
}

export interface AvatarU {
  avatarId: AvatarIdT
  name: SidekickNameT
  colors: UserColorsT
  isMsk: boolean
}

export const YouChatUser: ChatU = {
  name: "You",
  colors: YouColors,
}

export const UnknownUser: ChatU = {
  name: "Sidekick",
  colors: UnknownUserColors,
}

export const UnknownAvatar: AvatarU = {
  avatarId: 0,
  name: UnknownUser.name,
  colors: UnknownUserColors,
  isMsk: false,
}

// TODO:
// Currently we use only the name as the unique identifier of a ChatUser.
// Avatar id might also be a good proxy for having ids for ChatUsers since the backend doesn't even really have this data type.
// Avatar id unfortunately doesn't make sense on a message, only as an active value of a Conversation.
// Since we want to coordinate colors between chat messages in the history and active Avatars, we choose name as the unique key.
let nextUserColor = 0
const assignedUserColors: Record<SidekickNameT, UserColorsT> = {}

const getOrAssignColorsForUser = (name: SidekickNameT): UserColorsT => {
  if (assignedUserColors[name] === undefined) {
    assignedUserColors[name] = CHAT_BUBBLE_COLORS[nextUserColor]

    // Loop the colors
    if (nextUserColor >= CHAT_BUBBLE_COLORS.length - 1) {
      nextUserColor = 0
    } else {
      nextUserColor += 1
    }
  }

  return assignedUserColors[name]
}

export const userForMessage = (message: TMessage, currentMsk: MSKType | undefined): ChatU => {
  const isMemberMsg = message?.role === "member"

  if (isMemberMsg) {
    return YouChatUser
  }

  if (!(message && message.sidekick_name && message.active_esk_type) || !currentMsk) {
    return UnknownUser
  }

  const name = message.sidekick_name
  const eskType = message.active_esk_type

  // FIXME: MSK vs ESK detection needs to be reworked so it's unified and reliable. We'll need historical
  // conversations to contain the full set of sidekick fields so that their messages can associate that data.
  let colors: UserColorsT
  const isMsk = eskType === "HOST" || eskType === "PSK" || (currentMsk && currentMsk.name === name)

  if (isMsk) {
    colors = MskColors
  } else {
    colors = getOrAssignColorsForUser(name)
  }

  return {
    name,
    colors,
  }
}

export const avatarUserForConversation = (
  conversation: TConversation | undefined,
  currentMsk: MSKType | undefined,
): AvatarU => {
  // Helper function to get fallback avatar
  const getFallbackAvatar = (): AvatarU => {
    if (currentMsk) {
      return {
        avatarId: parseInt(currentMsk.avatar_id, 10),
        name: currentMsk.name,
        colors: MskColors,
        isMsk: true,
      }
    }
    return UnknownAvatar
  }

  // Early return if conversation is not available yet
  if (!conversation) {
    return getFallbackAvatar()
  }

  // Check for undefined values before processing
  if (
    conversation.sidekick_avatar_id === undefined ||
    conversation.sidekick_avatar_id === null ||
    conversation.sidekick_name === undefined ||
    conversation.sidekick_name === null
  ) {
    if (__DEV__) {
      console.error(
        "avatarUserForConversation: undefined avatarId or avatarName",
        conversation.sidekick_avatar_id,
        conversation.sidekick_name,
      )
    }
    return getFallbackAvatar()
  }

  const maybeAvatarId = Number(conversation.sidekick_avatar_id)
  const maybeAvatarName = conversation.sidekick_name as string
  const maybeActiveEskType = conversation.active_esk_type as string

  // Extra safeguard: check for NaN after Number conversion (shouldn't happen if backend is well-behaved)
  if (isNaN(maybeAvatarId)) {
    if (__DEV__) {
      console.error(
        "avatarUserForConversation: avatarId converted to NaN",
        conversation.sidekick_avatar_id,
        maybeAvatarId,
      )
    }
    return getFallbackAvatar()
  }

  // FIXME: cases when we aren't getting correct conversations back (could be client side bug that causes them to 404?)
  // 1. active_sidekick_type is ESK
  // 2. active_sidekick_type is MSK
  // 3. active_sidekick_type is undefined
  //   active_esk_type is HOST
  //   active_esk_type is a number
  //     in this last case we can't tell if it's MSK or ESK, could be an alternative MSK
  const avatarId = maybeAvatarId
  const name = maybeAvatarName

  // Determine if this is an MSK (Personal Sidekick)
  // We check multiple sources for reliability:
  // 1. Explicit sidekick type (MSK or PSK)
  // 2. ESK type mapping (HOST or PSK)
  // 3. Match with current MSK name or avatar ID
  const isMskByType =
    conversation.active_sidekick_type === "MSK" ||
    conversation.active_sidekick_type === "PSK" ||
    ((conversation.active_sidekick_type === undefined ||
      conversation.active_sidekick_type === null) &&
      (maybeActiveEskType === "HOST" || maybeActiveEskType === "PSK"))

  const isMskByMatch = !!(
    currentMsk &&
    (name === currentMsk.name || avatarId === parseInt(currentMsk.avatar_id, 10))
  )

  const isMsk = isMskByType || isMskByMatch

  // Stale MSK conversation: this is an MSK-type conversation but its sidekick doesn't match the
  // selected PSK (e.g. we just selected a new MSK and sync kept the previous conversation in MMKV).
  // Show the selected PSK's avatar instead. Only apply when isMskByType so we don't override ESK
  // conversations (which legitimately have a different sidekick and should show that avatar).
  if (
    currentMsk &&
    isMskByType &&
    name !== currentMsk.name &&
    avatarId !== parseInt(currentMsk.avatar_id, 10)
  ) {
    return getFallbackAvatar()
  }

  let colors: UserColorsT
  if (isMsk) {
    colors = MskColors
  } else {
    colors = getOrAssignColorsForUser(name)
  }

  return {
    avatarId,
    name,
    colors,
    isMsk,
  }
}
